import { map } from "lodash";
import nearestColor from "nearest-color";

import { colorMap } from "../constants/colorOptions";

const nearestColorFromSwatchMap = nearestColor.from(colorMap);

export const nearestSwatchColorToHex = hexStr => nearestColor(colorMap, hexStr);

export const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

export const decimalRgbToHex = (r, g, b) => rgbToHex(Math.floor(r * 255), Math.floor(g * 255), Math.floor(b * 255));

export const hexToRgb = hex => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    }
    : null;
};

export const scrivenerColorStringToHex = str => {
  return decimalRgbToHex(...map(str.split(" "), s => Number(s)));
};

export const hexToScrivenerColorString = hex => {
  const rgb = hexToRgb(hex);
  return `${rgb.r / 255.0} ${rgb.g / 255.0} ${rgb.b / 255}`;
};

export const scrivenerColorToSwatchColor = str => {
  const hex = scrivenerColorStringToHex(str);
  const nearest = nearestColorFromSwatchMap(hex);
  return nearest.name;
};

/**
 * linear color channel values, for use in contrast calculations.
 * See https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests
 */
const getLinearColorValue = color => {
  const c = color / 255;
  return c < 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

/**
 * Calculate the luminance for a color.
 * See https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests
 */
const getLuminance = hexColor => {
  const { r, g, b } = hexToRgb(hexColor);
  return 0.2126 * getLinearColorValue(r) + 0.7152 * getLinearColorValue(g) + 0.0722 * getLinearColorValue(b);
};

/**
 * Calculate the contrast ratio between two colors.
 * See https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests
 */
const getContrast = (backColor, frontColor) => {
  const backLum = getLuminance(backColor) + 0.05;
  const foreLum = getLuminance(frontColor) + 0.05;

  return Math.max(backLum, foreLum) / Math.min(backLum, foreLum);
};

/**
 *  Follow the function choose-contrast-color in contrastColor.scss
 *  See https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests
 *  @param {string} color
 *  @param {string} lightOption
 *  @param {string} darkOption
 */
export const chooseContrastColor = (color, lightOption, darkOption) => {
  const lightContrast = getContrast(color, lightOption);
  const darkContrast = getContrast(color, darkOption);

  return lightContrast > darkContrast ? lightOption : darkOption;
};

export const chooseContrastColorFavorLight = (color, lightOption, darkOption) => {
  const lightContrast = getContrast(color, lightOption);
  const darkContrast = getContrast(color, darkOption);
  const contrastLimit = 2;

  return lightContrast > contrastLimit ? lightOption : lightContrast > darkContrast ? lightOption : darkOption;
};

export const swatchColorToScrivenerColor = swatchColor => hexToScrivenerColorString(colorMap[swatchColor]);

/**
 * A JS version of the sass mix() function
 * So far accept hex values only
 *
 * Copy and modified from https://gist.github.com/jedfoster/7939513
 * Thanks to
 *   https://gist.github.com/jedfoster, https://gist.github.com/tenji73
 *
 * @param {string} color_1 Hex values
 * @param {string} color_2 Hex values
 * @param {number} weight 1 - 100
 */
export const mixColors = (color_1, color_2, weight) => {
  function d2h(d) {
    return d.toString(16);
  } // convert a decimal value to hex
  function h2d(h) {
    return parseInt(h, 16);
  } // convert a hex value to decimal

  if (color_1 && color_2) {
    weight = typeof weight !== "undefined" ? weight : 50; // set the weight to 50%, if that argument is omitted

    let color = "#";

    // Remove the initial #
    color_1 = color_1.replace(/#/g, "");
    color_2 = color_2.replace(/#/g, "");

    for (let i = 0; i <= 5; i += 2) {
      // loop through each of the 3 hex pairs—red, green, and blue
      const v1 = h2d(color_1.substr(i, 2)), // extract the current pairs
        v2 = h2d(color_2.substr(i, 2));
      // combine the current pairs from each source color, according to the specified weight
      let val = d2h(Math.floor(v2 + (v1 - v2) * (weight / 100.0)));

      while (val.length < 2) {
        val = "0" + val;
      } // prepend a '0' if val results in a single digit

      color += val; // concatenate val to our new color string
    }

    return color; // PROFIT!
  }
  return null;
};
